import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Button,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  ThemeProvider,
  createTheme,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../auth/AuthContext';
import { canManageInputs, getPageAuthSession, isStoredSessionCurrent } from '../utils/auth';
import ProductTechnicalSheetDialog from '../components/ProductTechnicalSheetDialog';
import PageMenuButton from '../components/PageMenuButton';
import {
  mapInventoryProductToSellableItem,
  normalizeKdsName,
  normalizePreparationDescription,
  resolveCatalogImageUrl,
  updateSharedCatalogProduct,
} from '../utils/catalog';
import {
  CATALOG_ADMIN_GROUPS_KEY,
  CATALOG_ADMIN_SUMMARY_KEY,
  readCatalogSessionJson,
  writeCatalogSessionJson,
} from '../utils/catalogSessionStorage';
import {
  applyOptionIdentityUpdates,
  buildManualProductCreationChanges,
  buildProductEditorChanges,
  cloneProductDraft,
  createCatalogMutationId,
  createSharedOptionGroupDraft,
  createSharedOptionDraft,
  updateProductOption,
  updateProductOptionGroup,
} from '../utils/productEditor';
import { prepareProductMediaUploads, uploadProductMedia } from '../utils/productMedia';

const API_URL = getApiUrl();

const IFOOD_IMAGE_BASE_URL = 'https://static-images.ifood.com.br/image/upload/';
const IFOOD_IMAGE_DEFAULT_TRANSFORM = 't_medium';
const IFOOD_IMAGE_DEFAULT_FOLDER = 'pratos';
const CATALOG_READ_TIMEOUT_MS = 15000;

const createTimedRead = (timeoutMs = CATALOG_READ_TIMEOUT_MS) => {
  const controller = new AbortController();
  let didTimeout = false;
  const timeout = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs);
  return {
    controller,
    clear: () => window.clearTimeout(timeout),
    toError: (error, message = 'A leitura excedeu o tempo limite. Tente novamente.') => {
      if (!didTimeout) return error;
      const timeoutError = new Error(message);
      timeoutError.name = 'TimeoutError';
      return timeoutError;
    },
  };
};

const apiFetchWithWriteTimeout = async (url, options = {}, timeoutMs = 20000) => {
  const controller = new AbortController();
  let timeout;
  const timeoutPromise = new Promise((resolve, reject) => {
    timeout = window.setTimeout(() => {
      const error = new Error('A gravação excedeu o tempo limite. Verifique o estado antes de tentar novamente.');
      error.name = 'TimeoutError';
      reject(error);
      controller.abort();
    }, timeoutMs);
  });
  try {
    return await Promise.race([
      apiFetch(url, { ...options, signal: controller.signal }),
      timeoutPromise,
    ]);
  } finally {
    window.clearTimeout(timeout);
  }
};

const isExactPageSessionCurrent = (session) => {
  const pageSession = getPageAuthSession();
  return pageSession?.token === session?.token
    && pageSession?.browserSessionId === session?.browserSessionId
    && String(pageSession?.user?.companyId || '') === String(session?.user?.companyId || '')
    && isStoredSessionCurrent(session);
};

const assertExactPageSessionCurrent = (session) => {
  if (isExactPageSessionCurrent(session)) return;
  const error = new Error('A sessão mudou durante a operação.');
  error.name = 'AbortError';
  throw error;
};

export const reorderCatalogGroups = (groups = [], sourceIndex, destinationIndex) => {
  if (
    !Array.isArray(groups)
    || sourceIndex === destinationIndex
    || sourceIndex < 0
    || destinationIndex < 0
    || sourceIndex >= groups.length
    || destinationIndex >= groups.length
  ) {
    return groups;
  }

  const next = [...groups];
  const [movedGroup] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, movedGroup);
  return next;
};

export const reorderCatalogGroupsFromDrop = (groups = [], result = {}) => {
  if (!result.source || !result.destination) return groups;
  return reorderCatalogGroups(groups, result.source.index, result.destination.index);
};

export const reorderCatalogProducts = (products = [], sourceIndex, destinationIndex) => {
  if (
    !Array.isArray(products)
    || sourceIndex === destinationIndex
    || sourceIndex < 0
    || destinationIndex < 0
    || sourceIndex >= products.length
    || destinationIndex >= products.length
  ) {
    return products;
  }

  const next = [...products];
  const [movedProduct] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, movedProduct);
  return next;
};

export const reorderCatalogProductsFromDrop = (products = [], result = {}) => {
  if (!result.source || !result.destination) return products;
  return reorderCatalogProducts(products, result.source.index, result.destination.index);
};

const sortCatalogProducts = (products = []) => [...products].sort((left, right) => (
  Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
  || Number(left.dbId || 0) - Number(right.dbId || 0)
));

const buildIfoodImageUrl = (logosUrls) => {
  if (!logosUrls || !Array.isArray(logosUrls) || logosUrls.length === 0) {
    return null;
  }
  const path = logosUrls[0]?.trim();
  if (!path) {
    return null;
  }
  if (
    path.startsWith('http://')
    || path.startsWith('https://')
    || path.startsWith('data:')
    || path.startsWith('/api/product-media/')
  ) {
    return path;
  }

  const normalizedPath = path.replace(/^\/+/, '');
  const transformMatch = normalizedPath.match(/^(t_[^/]+)\/(.*)$/);
  const transform = transformMatch ? transformMatch[1] : IFOOD_IMAGE_DEFAULT_TRANSFORM;
  let remainder = transformMatch ? transformMatch[2] : normalizedPath;

  const hasFolderPrefix = remainder.startsWith('pratos/') || remainder.startsWith('logos/') || remainder.startsWith('menus/');
  if (!hasFolderPrefix) {
    remainder = `${IFOOD_IMAGE_DEFAULT_FOLDER}/${remainder}`;
  }

  return `${IFOOD_IMAGE_BASE_URL}${transform}/${remainder}`;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1', // Indigo moderno
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#f59e0b', // Amber
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    success: {
      main: '#10b981', // Emerald
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444', // Red
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4f46e5 0%, #d97706 100%)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
  },
});

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const ensureShowInKds = (value) => value !== false;
const ensureShowInBalcao = (value) => value !== false;
const createEmptyProduct = () => ({
  dbId: null,
  merchantId: '',
  merchantName: '',
  catalogId: '',
  catalogContext: '',
  itemId: '',
  itemExternalCode: '',
  itemName: '',
  categoryName: '',
  catalogGroupId: null,
  itemDescription: '',
  preparationDescription: '',
  kdsName: '',
  itemPrice: { value: 0 },
  itemMinSalePrice: 0,
  itemQuantity: 0,
  itemUnit: '',
  logosUrls: [],
  itemOptionGroups: [],
  stock: 0,
  showInKds: true,
  showInBalcao: true,
});

const normalizeProductForSave = (product) => {
  const imageUrl = buildIfoodImageUrl(product.logosUrls) || product.imageUrl || null;
  const normalizedGroups = (product.itemOptionGroups || []).map((group) => ({
    ...group,
    options: (group.options || []).map((option) => ({
      ...option,
      showInKds: ensureShowInKds(option.showInKds),
    })),
  }));

  return {
    ...product,
    itemName: (product.itemName || '').trim(),
    itemExternalCode: (product.itemExternalCode || '').trim(),
    itemId: (product.itemId || '').trim(),
    categoryName: product.categoryName || '',
    catalogGroupId: product.catalogGroupId || null,
    itemDescription: product.itemDescription || '',
    preparationDescription: normalizePreparationDescription(product.preparationDescription),
    kdsName: normalizeKdsName(product.kdsName),
    imageUrl,
    showInKds: ensureShowInKds(product.showInKds),
    showInBalcao: ensureShowInBalcao(product.showInBalcao),
    itemOptionGroups: normalizedGroups,
  };
};

export const normalizeInventorySummaryProduct = (product = {}, fallback = {}) => {
  const mappedBase = product.itemName
    ? product
    : mapInventoryProductToSellableItem(product, product.override || {});
  const mapped = product.itemName ? mappedBase : {
    ...mappedBase,
    itemExternalCode: product.externalCode ?? mappedBase.itemExternalCode,
    itemExternalCodeOrigin: product.externalCodeOrigin ?? mappedBase.itemExternalCodeOrigin,
    hasStoredItemId: Boolean(product.itemId),
    preparationDescription: product.preparationDescription ?? mappedBase.preparationDescription,
    kdsName: product.kdsName ?? mappedBase.kdsName,
    showInKds: product.showInKds ?? mappedBase.showInKds,
    showInBalcao: product.showInBalcao ?? mappedBase.showInBalcao,
    itemMinSalePrice: product.itemMinSalePrice ?? mappedBase.itemMinSalePrice,
    itemQuantity: product.itemQuantity ?? mappedBase.itemQuantity,
    itemUnit: product.itemUnit ?? mappedBase.itemUnit,
    logosUrls: Array.isArray(product.logosUrls) ? product.logosUrls : mappedBase.logosUrls,
    itemOptionGroups: Array.isArray(product.itemOptionGroups) ? product.itemOptionGroups : mappedBase.itemOptionGroups,
  };
  const merged = { ...fallback, ...mapped };
  const hasDetailedGroups = Array.isArray(product.itemOptionGroups)
    || Array.isArray(product.payload?.itemOptionGroups)
    || Array.isArray(product.payload?.optionGroups);
  const hasDetailedImage = Array.isArray(product.logosUrls)
    || Array.isArray(product.payload?.logosUrls);
  const logosUrls = hasDetailedImage
    ? (Array.isArray(merged.logosUrls) ? merged.logosUrls : [])
    : (Array.isArray(fallback.logosUrls) ? fallback.logosUrls : (merged.logosUrls || []));
  const imageUrl = hasDetailedImage
    ? merged.imageUrl
    : (fallback.imageUrl || merged.imageUrl);
  return {
    ...merged,
    itemExternalCodeOrigin: product.externalCodeOrigin ?? merged.itemExternalCodeOrigin,
    dbId: Number(merged.dbId || product.id || fallback.dbId) || null,
    itemName: merged.itemName || product.name || fallback.itemName || 'Produto sem nome',
    categoryName: merged.categoryName || product.categoryName || fallback.categoryName || '',
    catalogGroupId: merged.catalogGroupId ? Number(merged.catalogGroupId) : null,
    catalogGroupOrder: Number(merged.catalogGroupOrder || 0),
    sortOrder: Number(merged.sortOrder || 0),
    source: merged.source || 'manual',
    hasStoredItemId: merged.hasStoredItemId === true,
    showInKds: ensureShowInKds(merged.showInKds),
    showInBalcao: ensureShowInBalcao(merged.showInBalcao),
    logosUrls,
    itemOptionGroups: (hasDetailedGroups ? merged.itemOptionGroups : (fallback.itemOptionGroups || merged.itemOptionGroups || [])).map((group) => ({
      ...group,
      options: (group.options || []).map((option) => ({
        ...option,
        showInKds: ensureShowInKds(option.showInKds),
      })),
    })),
    imageUrl: imageUrl || buildIfoodImageUrl(logosUrls),
    itemPrice: merged.itemPrice || { value: Number(merged.price || merged.unitPrice || 0) },
    unitPrice: Number(merged.itemPrice?.value ?? merged.price ?? merged.unitPrice ?? 0),
    stock: Number(merged.stock || 0),
    version: product.version || merged.version || fallback.version || null,
  };
};

const compactProductForAdminList = (product = {}) => {
  const imageUrl = String(product.imageUrl || '');
  const remoteLogos = (product.logosUrls || []).filter((entry) => /^https?:\/\//i.test(String(entry || '')));
  const compact = {
    ...product,
    imageUrl: imageUrl.startsWith('data:') ? null : (product.imageUrl || null),
    logosUrls: remoteLogos,
    itemOptionGroups: [],
    hasImage: product.hasImage === true || Boolean(imageUrl || product.logosUrls?.length),
  };
  delete compact.payload;
  return compact;
};

const Cardapio = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mayManageCatalog = canManageInputs(auth?.user);
  const [catalogReadOnly, setCatalogReadOnly] = useState(true);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(false);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editedProduct, setEditedProduct] = useState({});
  const [productFormError, setProductFormError] = useState('');
  const [savingProduct, setSavingProduct] = useState(false);
  const savingProductRef = useRef(false);
  const pendingProductMutationRef = useRef({ signature: '', mutationId: '' });
  const productPreviewUrlsRef = useRef([]);
  const [loadingProductEditor, setLoadingProductEditor] = useState(false);
  const [editorVersion, setEditorVersion] = useState(null);
  const [editorOriginal, setEditorOriginal] = useState(null);
  const [sharedOptionGroups, setSharedOptionGroups] = useState([]);
  const [sharedGroupManagerOpen, setSharedGroupManagerOpen] = useState(false);
  const [selectedSharedGroup, setSelectedSharedGroup] = useState(null);
  const [sharedGroupDraft, setSharedGroupDraft] = useState(null);
  const [sharedGroupDraftError, setSharedGroupDraftError] = useState('');
  const [savingSharedGroup, setSavingSharedGroup] = useState(false);
  const sharedGroupMutationRef = useRef({ signature: '', mutationId: '' });
  const [sharedGroupToDelete, setSharedGroupToDelete] = useState(null);
  const [deletingSharedGroup, setDeletingSharedGroup] = useState(false);
  const [groupToAttach, setGroupToAttach] = useState('');
  const catalogLoadRef = useRef({ generation: 0, controllers: [] });
  const editorLoadRef = useRef({ generation: 0, controller: null });
  const [savingVisibility, setSavingVisibility] = useState({});
  const [technicalSheetOpen, setTechnicalSheetOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [duplicatingProduct, setDuplicatingProduct] = useState(false);
  const [groupManagerOpen, setGroupManagerOpen] = useState(false);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ id: null, name: '' });
  const [groupFormError, setGroupFormError] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderDraft, setOrderDraft] = useState([]);
  const [orderError, setOrderError] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [productOrderDialogOpen, setProductOrderDialogOpen] = useState(false);
  const [productOrderGroupKey, setProductOrderGroupKey] = useState('');
  const [productOrderDraft, setProductOrderDraft] = useState([]);
  const [productOrderError, setProductOrderError] = useState('');
  const [savingProductOrder, setSavingProductOrder] = useState(false);
  const refreshSharedOptionGroups = useCallback(async (session = getPageAuthSession()) => {
    assertExactPageSessionCurrent(session);
    const response = await apiFetch(`${API_URL}/api/products/inventory/shared-option-groups`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false || !Array.isArray(data.groups)) {
      throw new Error(data.error || 'Falha ao carregar grupos de complementos.');
    }
    assertExactPageSessionCurrent(session);
    setSharedOptionGroups(data.groups);
    return data.groups;
  }, []);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [deleteGroupConfirmed, setDeleteGroupConfirmed] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const mayMutateCatalog = mayManageCatalog && !catalogReadOnly;

  const openSharedGroupManager = useCallback(async () => {
    setSharedGroupManagerOpen(true);
    try {
      await refreshSharedOptionGroups();
    } catch (loadError) {
      setError(loadError.message || 'Falha ao carregar grupos de complementos.');
    }
  }, [refreshSharedOptionGroups]);
  const openSharedGroupDetails = useCallback((group) => {
    if (!group?.definition) return;
    const definition = {
      ...group.definition,
      options: (group.definition.options || []).map((option) => ({ ...option })),
    };
    setSelectedSharedGroup(group);
    setSharedGroupDraft(definition);
    setSharedGroupDraftError('');
    sharedGroupMutationRef.current = { signature: '', mutationId: '' };
  }, []);
  const updateSharedGroupDraft = useCallback((changes) => {
    setSharedGroupDraft((current) => current ? { ...current, ...changes } : current);
  }, []);
  const updateSharedOptionDraft = useCallback((optionIndex, changes) => {
    setSharedGroupDraft((current) => current ? {
      ...current,
      options: current.options.map((option, index) => index === optionIndex ? { ...option, ...changes } : option),
    } : current);
  }, []);
  const addSharedOptionDraft = useCallback(() => {
    setSharedGroupDraft((current) => current ? {
      ...current,
      options: [...current.options, {
        name: '', description: '', price: { value: 0 }, showInKds: true,
        _draftOptionId: createCatalogMutationId(),
      }],
    } : current);
  }, []);
  const duplicateSharedOptionDraft = useCallback((optionIndex) => {
    setSharedGroupDraft((current) => {
      if (!current) return current;
      const source = current.options[optionIndex];
      if (!source) return current;
      const copy = { ...source, _draftOptionId: createCatalogMutationId() };
      for (const key of [
        'brutosSharedOptionId', 'optionId', 'id', 'itemId', 'productId', 'externalCode',
        'externalCodeOrigin', 'itemExternalCode', 'integrationCode', 'integrationId',
        'code', 'optionProductId', 'status', 'availability',
      ]) delete copy[key];
      return { ...current, options: [...current.options.slice(0, optionIndex + 1), copy, ...current.options.slice(optionIndex + 1)] };
    });
  }, []);
  const saveSharedGroupDetails = useCallback(async () => {
    if (!selectedSharedGroup || !sharedGroupDraft || savingSharedGroup || !mayMutateCatalog) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    setSavingSharedGroup(true);
    setSharedGroupDraftError('');
    try {
      let prepared = {
        ...sharedGroupDraft,
        options: [...sharedGroupDraft.options],
      };
      for (let index = 0; index < prepared.options.length; index += 1) {
        const option = prepared.options[index];
        if (!option._pendingImageFile) continue;
        if (Number(selectedSharedGroup.productCount || 0) <= 0) {
          throw new Error('Associe o grupo a um produto antes de enviar uma foto de complemento.');
        }
        const asset = await uploadProductMedia(API_URL, option._pendingImageFile);
        assertExactPageSessionCurrent(session);
        const oldPreview = option._previewImageUrl;
        prepared.options[index] = {
          ...option,
          imageUrl: asset.url,
          logosUrls: [asset.url],
          imagePath: '',
          _pendingImageFile: undefined,
          _previewImageUrl: undefined,
        };
        if (oldPreview) URL.revokeObjectURL?.(oldPreview);
        setSharedGroupDraft(prepared);
      }
      const definition = {
        ...prepared,
        options: prepared.options.map((option) => {
          const clean = { ...option };
          for (const key of Object.keys(clean)) if (key.startsWith('_')) delete clean[key];
          return clean;
        }),
      };
      const expectedVersion = Number(selectedSharedGroup.version);
      const signature = JSON.stringify({ id: selectedSharedGroup.id, expectedVersion, definition });
      if (sharedGroupMutationRef.current.signature !== signature) {
        sharedGroupMutationRef.current = { signature, mutationId: createCatalogMutationId() };
      }
      const mutationId = sharedGroupMutationRef.current.mutationId;
      const endpoint = `${API_URL}/api/products/inventory/shared-option-groups/${encodeURIComponent(selectedSharedGroup.id)}`;
      const readConfirmed = async () => {
        const response = await apiFetch(`${endpoint}/mutations/${encodeURIComponent(mutationId)}`);
        const data = await response.json().catch(() => ({}));
        return response.ok && data.success !== false ? data : null;
      };
      let response;
      try {
        assertExactPageSessionCurrent(session);
        response = await apiFetchWithWriteTimeout(endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mutationId, expectedVersion, definition }),
        });
      } catch (writeError) {
        assertExactPageSessionCurrent(session);
        const confirmed = await readConfirmed().catch(() => null);
        if (!confirmed) throw writeError;
        response = { ok: true, json: async () => confirmed };
      }
      let data = await response.json().catch(() => ({}));
      if (!response.ok && Number(response.status || 0) >= 500) {
        assertExactPageSessionCurrent(session);
        const confirmed = await readConfirmed().catch(() => null);
        if (confirmed) {
          data = confirmed;
          response = { ok: true };
        }
      }
      if (!response.ok || data.success === false || !data.group) {
        throw new Error(data.error || 'Falha ao salvar os complementos do grupo.');
      }
      assertExactPageSessionCurrent(session);
      const groups = await refreshSharedOptionGroups(session);
      const confirmedGroup = groups.find((group) => group.id === selectedSharedGroup.id);
      if (!confirmedGroup || Number(confirmedGroup.version) < Number(data.group.version)) {
        throw new Error('O grupo ainda não foi confirmado na releitura do catálogo. Tente novamente.');
      }
      setSelectedSharedGroup(confirmedGroup);
      setSharedGroupDraft({
        ...confirmedGroup.definition,
        options: (confirmedGroup.definition.options || []).map((option) => ({ ...option })),
      });
      setSharedGroupDraftError('');
      sharedGroupMutationRef.current = { signature: '', mutationId: '' };
    } catch (saveError) {
      if (saveError?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setSharedGroupDraftError(saveError.message || 'Falha ao salvar os complementos do grupo.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setSavingSharedGroup(false);
    }
  }, [mayMutateCatalog, refreshSharedOptionGroups, savingSharedGroup, selectedSharedGroup, sharedGroupDraft]);

  useEffect(() => {
    if (!editingProduct || !mayMutateCatalog) return undefined;
    const session = getPageAuthSession();
    let active = true;
    setSharedOptionGroups([]);
    refreshSharedOptionGroups(session).catch((loadError) => {
      if (active && isExactPageSessionCurrent(session)) setProductFormError(loadError.message || 'Falha ao carregar grupos compartilhados.');
    });
    return () => { active = false; };
  }, [editingProduct, mayMutateCatalog, refreshSharedOptionGroups]);

  const isRequestCurrent = useCallback((requestRef, generation, session) => {
    return requestRef.current.generation === generation
      && isExactPageSessionCurrent(session);
  }, []);

  const fetchProducts = useCallback(async ({ restoreCache = false, silent = false } = {}) => {
    const session = getPageAuthSession();
    const generation = catalogLoadRef.current.generation + 1;
    catalogLoadRef.current.controllers?.forEach((controller) => controller.abort());
    const timedRead = createTimedRead();
    const coverageRead = createTimedRead();
    catalogLoadRef.current = {
      generation,
      controllers: [timedRead.controller, coverageRead.controller],
    };
    const requestCurrent = () => isRequestCurrent(catalogLoadRef, generation, session);

    const cachedProducts = readCatalogSessionJson(CATALOG_ADMIN_SUMMARY_KEY, [], session);
    const cachedGroups = readCatalogSessionJson(CATALOG_ADMIN_GROUPS_KEY, [], session);
    if (restoreCache && requestCurrent()) {
      if (Array.isArray(cachedProducts) && cachedProducts.length) {
        setProducts(cachedProducts.map((product) => normalizeInventorySummaryProduct(product)));
        setLoading(false);
      } else if (!silent) {
        setLoading(true);
      }
      if (Array.isArray(cachedGroups) && cachedGroups.length) setGroups(cachedGroups);
      if ((cachedProducts?.length || cachedGroups?.length)) {
        setCatalogReadOnly(true);
        setWarning('Exibindo uma cópia local somente para consulta enquanto o cardápio é revalidado.');
      }
    } else if (!silent) {
      setLoading(true);
    }

    let groupsConfirmed = false;
    let coverageByProduct = new Map();
    const applyCoverage = (items) => items.map((product) => {
      const coverage = coverageByProduct.get(Number(product.dbId)) || {};
      return {
        ...product,
        technicalSheetCount: Number(coverage.itemCount ?? product.technicalSheetCount ?? 0),
        technicalSheetCost: Number(coverage.totalCost ?? product.technicalSheetCost ?? 0),
      };
    });

    const summaryPromise = apiFetch(`${API_URL}/api/products/inventory-summary`, {
      signal: timedRead.controller.signal,
    }).catch((error) => { throw timedRead.toError(error, 'A leitura dos produtos excedeu o tempo limite.'); }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !Array.isArray(data.products)) {
        throw new Error(data.error || 'Falha ao carregar produtos do cardápio.');
      }
      const normalized = applyCoverage(data.products.map((product) => normalizeInventorySummaryProduct(product)));
      if (requestCurrent()) {
        writeCatalogSessionJson(CATALOG_ADMIN_SUMMARY_KEY, normalized, session);
        setProducts(normalized);
        setLoading(false);
        setError(null);
        if (!groupsConfirmed && !(Array.isArray(cachedGroups) && cachedGroups.length)) {
          const derived = new Map();
          normalized.forEach((product) => {
            if (!product.catalogGroupId || derived.has(product.catalogGroupId)) return;
            derived.set(product.catalogGroupId, {
              id: product.catalogGroupId,
              name: product.catalogGroupName || product.categoryName || `Grupo ${product.catalogGroupId}`,
              sortOrder: product.catalogGroupOrder || 0,
            });
          });
          if (derived.size) setGroups([...derived.values()]);
        }
      }
      return normalized;
    });

    const groupsPromise = apiFetch(`${API_URL}/api/products/groups`, {
      signal: timedRead.controller.signal,
    }).catch((error) => { throw timedRead.toError(error, 'A leitura dos grupos excedeu o tempo limite.'); }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !Array.isArray(data.groups)) {
        throw new Error(data.error || 'Falha ao carregar grupos do cardápio.');
      }
      groupsConfirmed = true;
      if (requestCurrent()) {
        writeCatalogSessionJson(CATALOG_ADMIN_GROUPS_KEY, data.groups, session);
        setGroups(data.groups);
      }
      return data.groups;
    });

    const coverageOutcome = apiFetch(`${API_URL}/api/inputs/technical-sheet/coverage`, {
      signal: coverageRead.controller.signal,
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !Array.isArray(data.coverage)) {
        throw new Error(data.error || 'Cobertura de ficha técnica indisponível.');
      }
      coverageByProduct = new Map(data.coverage.map((item) => [Number(item.productId), item]));
      if (requestCurrent()) setProducts((current) => applyCoverage(current));
      return { ok: true };
    }).catch((error) => ({ ok: false, error: coverageRead.toError(error, 'A leitura das fichas técnicas excedeu o tempo limite.') }));

    const [summaryResult, groupsResult] = await Promise.allSettled([
      summaryPromise,
      groupsPromise,
    ]);
    timedRead.clear();
    if (!requestCurrent()) return false;

    const summaryOk = summaryResult.status === 'fulfilled';
    const groupsOk = groupsResult.status === 'fulfilled';
    setCatalogReadOnly(!(summaryOk && groupsOk));
    setLoading(false);

    if (!summaryOk && !(Array.isArray(cachedProducts) && cachedProducts.length)) {
      setError(summaryResult.reason?.message || 'Falha ao carregar produtos do cardápio.');
    }
    const failed = [];
    if (!summaryOk) failed.push('produtos');
    if (!groupsOk) failed.push('grupos');
    if (failed.length) {
      setWarning(
        `${failed.join(', ')} indisponível(is). ${summaryOk && groupsOk
          ? 'Os demais dados continuam disponíveis.'
          : 'A tela permanece somente para consulta até uma releitura completa.'}`
      );
    } else {
      if (!silent) setWarning(null);
      setError(null);
    }
    void coverageOutcome.then((outcome) => {
      coverageRead.clear();
      if (!outcome.ok && requestCurrent()) {
        setWarning((current) => current || 'Cobertura de fichas técnicas indisponível. Os demais dados continuam disponíveis.');
      }
    });
    return summaryOk && groupsOk;
  }, [isRequestCurrent]);

  const deleteSharedOptionGroup = useCallback(async () => {
    if (!sharedGroupToDelete || deletingSharedGroup || !mayMutateCatalog) return;
    const session = getPageAuthSession();
    setDeletingSharedGroup(true);
    try {
      assertExactPageSessionCurrent(session);
      const response = await apiFetch(`${API_URL}/api/products/inventory/shared-option-groups/${encodeURIComponent(sharedGroupToDelete.id)}`, {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.error || 'Falha ao excluir o grupo compartilhado.');
      assertExactPageSessionCurrent(session);
      setSharedOptionGroups((current) => current.filter((group) => group.id !== sharedGroupToDelete.id));
      setSharedGroupToDelete(null);
      void fetchProducts({ silent: true });
    } catch (deleteError) {
      if (deleteError?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setError(deleteError.message || 'Falha ao excluir o grupo compartilhado.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setDeletingSharedGroup(false);
    }
  }, [deletingSharedGroup, fetchProducts, mayMutateCatalog, sharedGroupToDelete]);

  // Filtrar produtos baseado na busca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.itemDescription?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [products, searchTerm]);

  const applyAuthoritativeProduct = useCallback((rawProduct, fallback = {}, version = null) => {
    const authoritative = normalizeInventorySummaryProduct(
      version ? { ...rawProduct, version } : rawProduct,
      fallback
    );
    const listProduct = compactProductForAdminList(authoritative);
    setProducts((current) => {
      const exists = current.some((item) => Number(item.dbId) === Number(authoritative.dbId));
      const next = exists
        ? current.map((item) => (Number(item.dbId) === Number(authoritative.dbId) ? listProduct : item))
        : [...current, listProduct];
      writeCatalogSessionJson(CATALOG_ADMIN_SUMMARY_KEY, next);
      return next;
    });
    setSelectedProduct((current) => (
      Number(current?.dbId) === Number(authoritative.dbId) ? authoritative : current
    ));
    return authoritative;
  }, []);

  const readConfirmedMutation = useCallback(async (productId, mutationId, session) => {
    const timedRead = createTimedRead(10000);
    try {
      const mutationPath = productId
        ? `/api/products/inventory/${productId}/mutations/${encodeURIComponent(mutationId)}`
        : `/api/products/inventory/mutations/${encodeURIComponent(mutationId)}`;
      const response = await apiFetch(
        `${API_URL}${mutationPath}`,
        { signal: timedRead.controller.signal }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !data.product) return null;
      return isExactPageSessionCurrent(session) ? data : null;
    } finally {
      timedRead.clear();
    }
  }, []);

  // O resumo não carrega payload/opções. A edição começa somente após a leitura
  // autoritativa do detalhe e da versão usada no controle de concorrência.
  const startEditingProduct = useCallback(async () => {
    if (!selectedProduct?.dbId || !mayMutateCatalog || loadingProductEditor) return;
    const session = getPageAuthSession();
    const generation = editorLoadRef.current.generation + 1;
    editorLoadRef.current.controller?.abort();
    const timedRead = createTimedRead();
    editorLoadRef.current = { generation, controller: timedRead.controller };
    setProductFormError('');
    setLoadingProductEditor(true);
    try {
      const response = await apiFetch(`${API_URL}/api/products/inventory/${selectedProduct.dbId}/editor`, {
        signal: timedRead.controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !data.product || !data.version) {
        throw new Error(data.error || 'Falha ao carregar os dados atuais do produto.');
      }
      if (!isRequestCurrent(editorLoadRef, generation, session)) return;
      const authoritative = normalizeInventorySummaryProduct(data.product, selectedProduct);
      setSelectedProduct(authoritative);
      setEditorOriginal(cloneProductDraft(authoritative));
      setEditedProduct(cloneProductDraft(authoritative));
      setEditorVersion(data.version);
      setEditingProduct(true);
    } catch (err) {
      const readError = timedRead.toError(err, 'A leitura do produto excedeu o tempo limite. Tente novamente.');
      if (readError?.name !== 'AbortError' && isRequestCurrent(editorLoadRef, generation, session)) {
        setProductFormError(readError.message || 'Falha ao carregar os dados atuais do produto.');
      }
    } finally {
      timedRead.clear();
      if (isRequestCurrent(editorLoadRef, generation, session)) setLoadingProductEditor(false);
    }
  }, [isRequestCurrent, loadingProductEditor, mayMutateCatalog, selectedProduct]);

  const addSelectedProductToBalcao = useCallback(async () => {
    if (!selectedProduct?.dbId || loadingProductEditor) return;
    const session = getPageAuthSession();
    const timedRead = createTimedRead();
    setProductFormError('');
    setLoadingProductEditor(true);
    try {
      const response = await apiFetch(`${API_URL}/api/products/inventory/${selectedProduct.dbId}/editor`, {
        signal: timedRead.controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !data.product) {
        throw new Error(data.error || 'Falha ao carregar o produto para o Balcão.');
      }
      if (!isExactPageSessionCurrent(session)) return;
      navigate('/balcao', {
        state: { addProduct: normalizeInventorySummaryProduct(data.product, selectedProduct) },
      });
    } catch (err) {
      const readError = timedRead.toError(err, 'A leitura do produto excedeu o tempo limite. Tente novamente.');
      if (readError?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setProductFormError(readError.message || 'Falha ao carregar o produto para o Balcão.');
      }
    } finally {
      timedRead.clear();
      if (isExactPageSessionCurrent(session)) setLoadingProductEditor(false);
    }
  }, [loadingProductEditor, navigate, selectedProduct]);

  const closeProductDialog = useCallback(() => {
    editorLoadRef.current.controller?.abort();
    editorLoadRef.current = { generation: editorLoadRef.current.generation + 1, controller: null };
    setEditingProduct(false);
    setIsAddingProduct(false);
    setEditedProduct({});
    setEditorOriginal(null);
    setEditorVersion(null);
    setLoadingProductEditor(false);
    pendingProductMutationRef.current = { signature: '', mutationId: '' };
    setProductFormError('');
    setSelectedProduct(null);
    setGroupToAttach('');
    setTechnicalSheetOpen(false);
    productPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL?.(url));
    productPreviewUrlsRef.current = [];
  }, []);

  const startAddingProduct = useCallback(() => {
    const emptyProduct = createEmptyProduct();
    setSelectedProduct(emptyProduct);
    setEditorOriginal(cloneProductDraft(emptyProduct));
    setEditedProduct(cloneProductDraft(emptyProduct));
    setEditorVersion(null);
    setProductFormError('');
    setEditingProduct(true);
    setIsAddingProduct(true);
  }, []);

  const saveProductVisibility = useCallback(async (product, changes = {}) => {
    if (!product?.dbId || !mayMutateCatalog) return;
    const session = getPageAuthSession();
    const detailRead = createTimedRead();
    setSavingVisibility((prev) => ({ ...prev, [product.dbId]: true }));
    try {
      let detailResponse;
      try {
        detailResponse = await apiFetch(`${API_URL}/api/products/inventory/${product.dbId}/editor`, {
          signal: detailRead.controller.signal,
        });
      } finally {
        detailRead.clear();
      }
      const detailData = await detailResponse.json().catch(() => ({}));
      if (!detailResponse.ok || detailData.success === false || !detailData.product || !detailData.version) {
        throw new Error(detailData.error || 'Falha ao carregar a versão atual do produto.');
      }
      if (!isExactPageSessionCurrent(session)) return;

      const requestedChanges = {};
      if (Object.prototype.hasOwnProperty.call(changes, 'showInKds')) {
        requestedChanges.showInKds = ensureShowInKds(changes.showInKds);
      }
      if (Object.prototype.hasOwnProperty.call(changes, 'showInBalcao')) {
        requestedChanges.showInBalcao = ensureShowInBalcao(changes.showInBalcao);
      }
      const mutationId = createCatalogMutationId();
      let inventoryResponse;
      try {
        assertExactPageSessionCurrent(session);
        inventoryResponse = await apiFetchWithWriteTimeout(`${API_URL}/api/products/inventory/${product.dbId}/editor`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mutationId,
            expectedVersion: detailData.version,
            changes: requestedChanges,
          }),
        });
      } catch (requestError) {
        assertExactPageSessionCurrent(session);
        const confirmed = await readConfirmedMutation(product.dbId, mutationId, session).catch(() => null);
        if (!confirmed) throw requestError;
        inventoryResponse = { ok: true, json: async () => confirmed };
      }
      let inventoryData = await inventoryResponse.json().catch(() => ({}));
      if (!inventoryResponse.ok && Number(inventoryResponse.status || 0) >= 500) {
        assertExactPageSessionCurrent(session);
        const confirmed = await readConfirmedMutation(product.dbId, mutationId, session).catch(() => null);
        if (confirmed) {
          inventoryData = confirmed;
          inventoryResponse = { ok: true };
        }
      }
      if (!inventoryResponse.ok || inventoryData.success === false || !inventoryData.product) {
        throw new Error(inventoryData.error || 'Falha ao salvar visibilidade do produto');
      }
      if (!isExactPageSessionCurrent(session)) return;
      const detailProduct = normalizeInventorySummaryProduct(detailData.product, product);
      const confirmedProduct = applyAuthoritativeProduct(
        inventoryData.product,
        { ...detailProduct, ...requestedChanges },
        inventoryData.version
      );
      updateSharedCatalogProduct(confirmedProduct);
      void fetchProducts({ silent: true });
    } catch (err) {
      const visibleError = detailRead.toError(err, 'A leitura do produto excedeu o tempo limite. Tente novamente.');
      if (visibleError?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        console.error('Erro ao salvar visibilidade do produto:', visibleError);
        setError(visibleError.message || 'Falha ao salvar visibilidade do produto.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) {
        setSavingVisibility((prev) => {
          const next = { ...prev };
          delete next[product.dbId];
          return next;
        });
      }
    }
  }, [applyAuthoritativeProduct, fetchProducts, mayMutateCatalog, readConfirmedMutation]);

  const duplicateSelectedProduct = useCallback(async () => {
    if (!selectedProduct?.dbId || duplicatingProduct || !mayMutateCatalog) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    setDuplicatingProduct(true);
    setProductFormError('');
    setError(null);
    try {
      const response = await apiFetch(`${API_URL}/api/products/inventory/${selectedProduct.dbId}/duplicate`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao duplicar produto.');
      }
      if (!data.product) throw new Error('O servidor não retornou a cópia criada.');
      assertExactPageSessionCurrent(session);
      applyAuthoritativeProduct(data.product);
      closeProductDialog();
      void fetchProducts({ silent: true });
      setWarning('Produto duplicado com todos os complementos. O Código Externo do novo produto ficou em branco.');
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        console.error('Erro ao duplicar produto:', err);
        setProductFormError(err.message || 'Falha ao duplicar produto.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setDuplicatingProduct(false);
    }
  }, [applyAuthoritativeProduct, closeProductDialog, duplicatingProduct, fetchProducts, mayMutateCatalog, selectedProduct]);

  // Salvar edição do produto
  const saveEditingProduct = useCallback(async () => {
    if (savingProductRef.current || (!isAddingProduct && !mayMutateCatalog)) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    savingProductRef.current = true;
    setSavingProduct(true);
    try {
      setError(null);
      setProductFormError('');

      let draftForSave = editedProduct;
      const prepared = await prepareProductMediaUploads(
        editedProduct,
        async (file) => {
          assertExactPageSessionCurrent(session);
          const uploaded = await uploadProductMedia(API_URL, file);
          assertExactPageSessionCurrent(session);
          return uploaded;
        }
      );
      assertExactPageSessionCurrent(session);
      if (prepared !== editedProduct) {
        draftForSave = prepared;
        setEditedProduct(cloneProductDraft(prepared));
        productPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL?.(url));
        productPreviewUrlsRef.current = [];
      }
      const normalizedProduct = normalizeProductForSave(draftForSave);
      if (!normalizedProduct.itemName) {
        throw new Error('Informe o nome do produto');
      }

      const productForSave = normalizedProduct;

      if (!isAddingProduct && !normalizedProduct.dbId) {
        throw new Error('Produto sem identificador interno para atualizar no cardápio');
      }

      if (!isAddingProduct && (!editorVersion || !editorOriginal)) {
        throw new Error('A versão atual do produto não foi carregada. Feche e abra a edição novamente.');
      }

      const changes = isAddingProduct
        ? buildManualProductCreationChanges(productForSave)
        : buildProductEditorChanges(editorOriginal, productForSave);
      if (!isAddingProduct && Object.keys(changes).length === 0) {
        closeProductDialog();
        return;
      }

      const mutationSignature = JSON.stringify({
        productId: normalizedProduct.dbId || 'new',
        expectedVersion: isAddingProduct ? null : editorVersion,
        changes,
      });
      if (pendingProductMutationRef.current.signature !== mutationSignature) {
        pendingProductMutationRef.current = {
          signature: mutationSignature,
          mutationId: createCatalogMutationId(),
        };
      }
      const mutationId = pendingProductMutationRef.current.mutationId;
      let inventoryResponse;
      try {
        assertExactPageSessionCurrent(session);
        inventoryResponse = await apiFetchWithWriteTimeout(`${API_URL}/api/products/inventory${isAddingProduct ? '/editor' : `/${normalizedProduct.dbId}/editor`}`, {
        method: isAddingProduct ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mutationId,
          changes,
          ...(!isAddingProduct ? { expectedVersion: editorVersion } : {}),
        }),
        });
      } catch (requestError) {
        assertExactPageSessionCurrent(session);
        const confirmed = await readConfirmedMutation(
          isAddingProduct ? null : normalizedProduct.dbId,
          mutationId,
          session
        ).catch(() => null);
        if (!confirmed) throw requestError;
        inventoryResponse = { ok: true, json: async () => confirmed };
      }

      let inventoryData = await inventoryResponse.json().catch(() => ({}));
      if (!inventoryResponse.ok && Number(inventoryResponse.status || 0) >= 500) {
        assertExactPageSessionCurrent(session);
        const confirmed = await readConfirmedMutation(
          isAddingProduct ? null : normalizedProduct.dbId,
          mutationId,
          session
        ).catch(() => null);
        if (confirmed) {
          inventoryData = confirmed;
          inventoryResponse = { ok: true };
        }
      }
      if (!inventoryResponse.ok || inventoryData.success === false) {
        throw new Error(inventoryData.error || 'Falha ao salvar produto no cardápio');
      }
      if (!isExactPageSessionCurrent(session)) return;

      const responseProduct = inventoryData.product;
      if (!responseProduct) throw new Error('O servidor não retornou o produto salvo.');
      const confirmedProduct = applyOptionIdentityUpdates(productForSave, inventoryData.optionIdentityUpdates);
      const savedProduct = applyAuthoritativeProduct(
        responseProduct,
        { ...confirmedProduct, dbId: responseProduct.id || normalizedProduct.dbId },
        inventoryData.version
      );
      updateSharedCatalogProduct(savedProduct, { append: isAddingProduct });
      pendingProductMutationRef.current = { signature: '', mutationId: '' };
      closeProductDialog();
      void fetchProducts({ silent: true });
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        console.error('Erro ao salvar produto:', err);
        setProductFormError(err.message || 'Falha ao salvar produto');
      }
    } finally {
      savingProductRef.current = false;
      if (isExactPageSessionCurrent(session)) setSavingProduct(false);
    }
  }, [
    applyAuthoritativeProduct,
    closeProductDialog,
    editedProduct,
    editorOriginal,
    editorVersion,
    fetchProducts,
    isAddingProduct,
    mayMutateCatalog,
    readConfirmedMutation,
  ]);

  // Cancelar edição
  const cancelEditingProduct = useCallback(() => {
    closeProductDialog();
  }, [closeProductDialog]);

  const deleteSelectedProduct = useCallback(async () => {
    if (!mayMutateCatalog) return;
    if (!selectedProduct?.dbId) {
      setError('Produto sem identificador interno para excluir.');
      return;
    }

    const confirmed = window.confirm(
      `Excluir "${selectedProduct.itemName}" do cardápio operacional do Brutos?\n\nEsta ação não apaga o produto no iFood. Produtos com ficha técnica vinculada serão apenas inativados para preservar histórico.`
    );
    if (!confirmed) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);

    setDeletingProduct(true);
    try {
      setError(null);
      setWarning(null);
      const response = await apiFetch(`${API_URL}/api/products/inventory/${selectedProduct.dbId}`, {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao excluir produto.');
      }
      assertExactPageSessionCurrent(session);

      setProducts((prev) => {
        const next = prev.filter((product) => product.dbId !== selectedProduct.dbId);
        writeCatalogSessionJson(CATALOG_ADMIN_SUMMARY_KEY, next);
        return next;
      });
      setFilteredProducts((prev) => prev.filter((product) => product.dbId !== selectedProduct.dbId));
      setSelectedProduct(null);
      setEditingProduct(false);
      setEditedProduct(null);
      if (data.action === 'inactivated') {
        setWarning(data.message || 'Produto inativado no cardápio operacional.');
      }
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        console.error('Erro ao excluir produto:', err);
        setError(err.message || 'Falha ao excluir produto');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setDeletingProduct(false);
    }
  }, [mayMutateCatalog, selectedProduct]);

  const openGroupForm = useCallback((group = null) => {
    setGroupManagerOpen(false);
    setGroupForm({ id: group?.id || null, name: group?.name || '' });
    setGroupFormError('');
    setGroupFormOpen(true);
  }, []);

  const saveGroup = useCallback(async () => {
    if (!mayMutateCatalog) return;
    const name = String(groupForm.name || '').trim();
    if (!name) {
      setGroupFormError('Informe o nome do grupo.');
      return;
    }

    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    setSavingGroup(true);
    setGroupFormError('');
    try {
      const response = await apiFetch(
        `${API_URL}/api/products/groups${groupForm.id ? `/${groupForm.id}` : ''}`,
        {
          method: groupForm.id ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao salvar grupo.');
      }
      assertExactPageSessionCurrent(session);
      setGroupFormOpen(false);
      await fetchProducts();
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setGroupFormError(err.message || 'Falha ao salvar grupo.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setSavingGroup(false);
    }
  }, [fetchProducts, groupForm, mayMutateCatalog]);

  const openOrderDialog = useCallback(() => {
    setOrderDraft(groups.map((group) => ({ ...group })));
    setOrderError('');
    setOrderDialogOpen(true);
  }, [groups]);

  const handleGroupDragEnd = useCallback(({ source, destination }) => {
    setOrderDraft((current) => (
      reorderCatalogGroupsFromDrop(current, { source, destination })
    ));
  }, []);

  const saveGroupOrder = useCallback(async () => {
    if (!mayMutateCatalog) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    setSavingOrder(true);
    setOrderError('');
    try {
      const response = await apiFetch(`${API_URL}/api/products/groups/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupIds: orderDraft.map((group) => group.id) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao salvar ordem dos grupos.');
      }
      assertExactPageSessionCurrent(session);
      setOrderDialogOpen(false);
      await fetchProducts();
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setOrderError(err.message || 'Falha ao salvar ordem dos grupos.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setSavingOrder(false);
    }
  }, [fetchProducts, mayMutateCatalog, orderDraft]);

  const requestDeleteGroup = useCallback((group) => {
    setGroupManagerOpen(false);
    setDeleteGroup(group);
    setDeleteGroupConfirmed(false);
  }, []);

  const confirmDeleteGroup = useCallback(async () => {
    if (!deleteGroup?.id || !deleteGroupConfirmed || !mayMutateCatalog) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    setDeletingGroup(true);
    setError(null);
    try {
      const response = await apiFetch(`${API_URL}/api/products/groups/${deleteGroup.id}`, {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao excluir grupo.');
      }
      assertExactPageSessionCurrent(session);
      setDeleteGroup(null);
      setDeleteGroupConfirmed(false);
      await fetchProducts();
      assertExactPageSessionCurrent(session);
      setWarning(data.message || 'Grupo e seus produtos foram removidos do cardápio local.');
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setError(err.message || 'Falha ao excluir grupo.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setDeletingGroup(false);
    }
  }, [deleteGroup, deleteGroupConfirmed, fetchProducts, mayMutateCatalog]);

  useEffect(() => {
    fetchProducts({ restoreCache: true });
    return () => {
      catalogLoadRef.current.controllers?.forEach((controller) => controller.abort());
      catalogLoadRef.current.generation += 1;
      editorLoadRef.current.controller?.abort();
    };
  }, [fetchProducts]);

  const orderedGroups = [...groups].sort((left, right) => (
    Number(left.sortOrder || 0) - Number(right.sortOrder || 0)
    || Number(left.id || 0) - Number(right.id || 0)
  ));
  const groupedProductSections = orderedGroups
    .map((group) => ({
      group,
      products: sortCatalogProducts(filteredProducts.filter((product) => Number(product.catalogGroupId) === Number(group.id))),
    }))
    .filter((section) => section.products.length > 0);
  const activeGroupIds = new Set(orderedGroups.map((group) => Number(group.id)));
  const ungroupedProducts = sortCatalogProducts(filteredProducts.filter((product) => (
    !product.catalogGroupId || !activeGroupIds.has(Number(product.catalogGroupId))
  )));

  const productOrderSections = [
    ...orderedGroups.map((group) => ({
      key: `group-${group.id}`,
      group,
      label: group.name,
      products: sortCatalogProducts(products.filter((product) => Number(product.catalogGroupId) === Number(group.id))),
    })).filter((section) => section.products.length > 0),
    ...(() => {
      const legacy = sortCatalogProducts(products.filter((product) => (
        !product.catalogGroupId || !activeGroupIds.has(Number(product.catalogGroupId))
      )));
      return legacy.length ? [{ key: 'ungrouped', group: null, label: 'Sem grupo', products: legacy }] : [];
    })(),
  ];
  const selectedProductOrderSection = productOrderSections.find((section) => section.key === productOrderGroupKey)
    || productOrderSections[0]
    || null;

  const openProductOrderDialog = () => {
    const initialSection = productOrderSections.find((section) => section.products.length > 1)
      || productOrderSections[0]
      || null;
    setProductOrderGroupKey(initialSection?.key || '');
    setProductOrderDraft(initialSection?.products || []);
    setProductOrderError('');
    setProductOrderDialogOpen(true);
  };

  const changeProductOrderGroup = (event) => {
    const nextSection = productOrderSections.find((section) => section.key === event.target.value);
    setProductOrderGroupKey(event.target.value);
    setProductOrderDraft(nextSection?.products || []);
    setProductOrderError('');
  };

  const handleProductDragEnd = ({ source, destination }) => {
    setProductOrderDraft((current) => reorderCatalogProductsFromDrop(current, { source, destination }));
  };

  const saveProductOrder = async () => {
    if (!selectedProductOrderSection || !mayMutateCatalog) return;
    const session = getPageAuthSession();
    assertExactPageSessionCurrent(session);
    setSavingProductOrder(true);
    setProductOrderError('');
    try {
      const response = await apiFetch(`${API_URL}/api/products/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          catalogGroupId: selectedProductOrderSection.group?.id || null,
          productIds: productOrderDraft.map((product) => product.dbId),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao salvar ordem dos produtos.');
      }
      assertExactPageSessionCurrent(session);
      setProductOrderDialogOpen(false);
      await fetchProducts();
    } catch (err) {
      if (err?.name !== 'AbortError' && isExactPageSessionCurrent(session)) {
        setProductOrderError(err.message || 'Falha ao salvar ordem dos produtos.');
      }
    } finally {
      if (isExactPageSessionCurrent(session)) setSavingProductOrder(false);
    }
  };

  const renderProductCard = (product) => (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.dbId || product.itemId}>
      <Card
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
        onClick={() => setSelectedProduct(product)}
      >
        {product.imageUrl && (
          <CardMedia
            component="img"
            height="140"
            image={resolveCatalogImageUrl(product.imageUrl)}
            alt={product.itemName}
          />
        )}
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom noWrap>
            {product.itemName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {product.categoryName || 'Sem grupo'}
          </Typography>
          <Box sx={{ mb: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {product.isPizza ? (
              <Chip size="small" color="primary" variant="outlined" label="Pizza" />
            ) : null}
            <Chip
              size="small"
              color={Number(product.technicalSheetCount || 0) > 0 ? 'success' : 'warning'}
              label={Number(product.technicalSheetCount || 0) > 0 ? 'Ficha ok' : 'Sem ficha'}
            />
          </Box>
          <Typography variant="body2" sx={{ mb: 2, flex: 1 }}>
            {product.itemDescription || 'Sem descrição'}
          </Typography>
          <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
            {formatCurrency(product.itemPrice?.value)}
          </Typography>
          <Box
            sx={{
              mt: 'auto',
              pt: 1,
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={ensureShowInKds(product.showInKds)}
                  disabled={!mayMutateCatalog || Boolean(savingVisibility[product.dbId])}
                  onChange={async (event) => {
                    try {
                      setError(null);
                      await saveProductVisibility(product, { showInKds: event.target.checked });
                    } catch (err) {
                      if (err?.name !== 'AbortError') {
                        console.error('Erro ao salvar visibilidade no KDS:', err);
                        setError(err.message || 'Falha ao salvar visibilidade do produto');
                      }
                    }
                  }}
                />
              }
              label="KDS"
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={ensureShowInBalcao(product.showInBalcao)}
                  disabled={!mayMutateCatalog || Boolean(savingVisibility[product.dbId])}
                  onChange={async (event) => {
                    try {
                      setError(null);
                      await saveProductVisibility(product, { showInBalcao: event.target.checked });
                    } catch (err) {
                      if (err?.name !== 'AbortError') {
                        console.error('Erro ao salvar visibilidade no Balcão:', err);
                        setError(err.message || 'Falha ao salvar visibilidade do produto');
                      }
                    }
                  }}
                />
              }
              label="Balcão"
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 1.5, md: 2 }, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <PageMenuButton />
          <Typography variant="h4">Gerenciamento de Cardápio</Typography>
        </Box>

      <Paper sx={{
        p: 2,
        mb: 2,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 3,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: { xs: '100%', md: 320 },
            backgroundColor: '#f1f5f9',
            borderRadius: 2,
            px: 2,
            py: 1,
            border: '1px solid #e2e8f0'
          }}>
            <SearchIcon sx={{ color: 'primary.main', mr: 1 }} />
            <InputBase
              placeholder="Buscar produtos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, '& .MuiInputBase-input::placeholder': { color: 'text.secondary' } }}
            />
          </Box>
          {mayManageCatalog && (
            <Button
              variant="outlined"
              startIcon={<CategoryOutlinedIcon />}
              onClick={() => setGroupManagerOpen(true)}
              disabled={catalogReadOnly}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto', md: '0 0 auto' }, minHeight: 44 }}
            >
              Gerenciar grupos
            </Button>
          )}
          {mayManageCatalog && (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={openSharedGroupManager}
              disabled={catalogReadOnly}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto', md: '0 0 auto' }, minHeight: 44 }}
            >
              Gerenciar complementos
            </Button>
          )}
          {mayManageCatalog && (
            <Button
              variant="outlined"
              startIcon={<SortOutlinedIcon />}
              onClick={openOrderDialog}
              disabled={catalogReadOnly || groups.length < 2}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto', md: '0 0 auto' }, minHeight: 44 }}
            >
              Organizar grupos
            </Button>
          )}
          {mayManageCatalog && (
            <Button
              variant="outlined"
              startIcon={<SortOutlinedIcon />}
              onClick={openProductOrderDialog}
              disabled={catalogReadOnly || !productOrderSections.some((section) => section.products.length > 1)}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto', md: '0 0 auto' }, minHeight: 44 }}
            >
              Organizar produtos
            </Button>
          )}
          {mayManageCatalog && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startAddingProduct}
              disabled={catalogReadOnly}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto', md: '0 0 auto' }, minHeight: 44 }}
            >
              Adicionar
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {warning}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {groupedProductSections.map(({ group, products: groupProducts }) => (
            <Box component="section" aria-labelledby={`catalog-group-${group.id}`} key={group.id}>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  flexWrap: 'wrap',
                }}
              >
                <Typography id={`catalog-group-${group.id}`} variant="h5" sx={{ fontWeight: 750 }}>
                  {group.name}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${groupProducts.length} ${groupProducts.length === 1 ? 'produto' : 'produtos'}`}
                />
              </Box>
              <Grid container spacing={3}>
                {groupProducts.map(renderProductCard)}
              </Grid>
            </Box>
          ))}
          {ungroupedProducts.length > 0 && (
            <Box component="section" aria-labelledby="catalog-group-ungrouped">
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography id="catalog-group-ungrouped" variant="h5" sx={{ fontWeight: 750 }}>
                  Sem grupo
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${ungroupedProducts.length} ${ungroupedProducts.length === 1 ? 'produto' : 'produtos'}`}
                />
              </Box>
              <Grid container spacing={3}>
                {ungroupedProducts.map(renderProductCard)}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {filteredProducts.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'Nenhum produto encontrado' : 'Nenhum produto disponível'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={groupManagerOpen}
        onClose={() => setGroupManagerOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="catalog-group-manager-title"
      >
        <DialogTitle id="catalog-group-manager-title">Gerenciar grupos</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openGroupForm()}>
              Criar grupo
            </Button>
          </Box>
          {orderedGroups.length === 0 ? (
            <Typography color="text.secondary">Nenhum grupo criado.</Typography>
          ) : (
            <List disablePadding aria-label="Grupos do cardápio">
              {orderedGroups.map((group, index) => (
                <React.Fragment key={group.id}>
                  <ListItem
                    sx={{
                      px: { xs: 0, sm: 1 },
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <ListItemText
                      primary={group.name}
                      secondary={`${Number(group.productCount || 0)} produto(s) • ${group.source === 'ifood' ? 'Importado do iFood' : 'Criado no Brutos'}`}
                    />
                    <IconButton
                      aria-label={`Editar grupo ${group.name}`}
                      onClick={() => openGroupForm(group)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      aria-label={`Excluir grupo ${group.name}`}
                      onClick={() => requestDeleteGroup(group)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </ListItem>
                  {index < orderedGroups.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupManagerOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={groupFormOpen}
        onClose={() => !savingGroup && setGroupFormOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="catalog-group-form-title"
      >
        <DialogTitle id="catalog-group-form-title">
          {groupForm.id ? 'Editar grupo' : 'Criar grupo'}
        </DialogTitle>
        <DialogContent>
          {groupFormError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{groupFormError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label="Nome do grupo"
            margin="normal"
            value={groupForm.name}
            onChange={(event) => setGroupForm((current) => ({ ...current, name: event.target.value }))}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !savingGroup) saveGroup();
            }}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={savingGroup} onClick={() => setGroupFormOpen(false)}>Cancelar</Button>
          <Button disabled={savingGroup} variant="contained" onClick={saveGroup}>
            {savingGroup ? 'Salvando...' : 'Salvar grupo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={orderDialogOpen}
        onClose={() => !savingOrder && setOrderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="catalog-group-order-title"
      >
        <DialogTitle id="catalog-group-order-title">Organizar grupos</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Segure e arraste os grupos para definir a ordem exibida no gerenciamento do cardápio.
          </Typography>
          {orderError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {orderError}
            </Alert>
          )}
          <DragDropContext onDragEnd={handleGroupDragEnd}>
            <Droppable droppableId="catalog-group-order">
              {(provided, snapshot) => (
                <List
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  disablePadding
                  aria-label="Ordem dos grupos"
                  aria-live="polite"
                  sx={{
                    borderRadius: 2,
                    bgcolor: snapshot.isDraggingOver ? 'rgba(99, 102, 241, 0.06)' : 'transparent',
                    transition: 'background-color 160ms ease',
                  }}
                >
                  {orderDraft.map((group, index) => (
                    <Draggable
                      key={group.id}
                      draggableId={String(group.id)}
                      index={index}
                      isDragDisabled={savingOrder}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <ListItem
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={dragProvided.draggableProps.style}
                          sx={{
                            px: { xs: 0.5, sm: 1 },
                            gap: 1,
                            minHeight: 64,
                            borderBottom: index < orderDraft.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            borderRadius: dragSnapshot.isDragging ? 2 : 0,
                            bgcolor: dragSnapshot.isDragging ? 'background.paper' : 'transparent',
                            boxShadow: dragSnapshot.isDragging ? 6 : 'none',
                            userSelect: 'none',
                            transition: 'background-color 160ms ease, box-shadow 160ms ease',
                          }}
                        >
                          <Box
                            component="span"
                            {...dragProvided.dragHandleProps}
                            aria-label={`Arrastar ${group.name}, posição ${index + 1}`}
                            sx={{
                              width: 44,
                              height: 44,
                              flexShrink: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.secondary',
                              borderRadius: '50%',
                              cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab',
                              touchAction: 'none',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                              '&:focus-visible': {
                                outline: '3px solid rgba(99, 102, 241, 0.35)',
                                outlineOffset: -3,
                              },
                            }}
                          >
                            <DragIndicatorIcon />
                          </Box>
                          <Chip size="small" label={index + 1} aria-label={`Posição ${index + 1}`} />
                          <ListItemText primary={group.name} />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button disabled={savingOrder} onClick={() => setOrderDialogOpen(false)}>Cancelar</Button>
          <Button disabled={savingOrder || orderDraft.length < 2} variant="contained" onClick={saveGroupOrder}>
            {savingOrder ? 'Salvando...' : 'Salvar ordem'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={productOrderDialogOpen}
        onClose={() => !savingProductOrder && setProductOrderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        aria-labelledby="catalog-product-order-title"
      >
        <DialogTitle id="catalog-product-order-title">Organizar produtos</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Escolha um grupo e arraste os produtos para definir a ordem exibida no Cardápio, Balcão e cardápio da mesa.
          </Typography>
          <TextField
            select
            fullWidth
            label="Grupo de produtos"
            value={selectedProductOrderSection?.key || ''}
            onChange={changeProductOrderGroup}
            disabled={savingProductOrder || productOrderSections.length < 2}
            sx={{ mb: 2 }}
          >
            {productOrderSections.map((section) => (
              <MenuItem key={section.key} value={section.key}>
                {section.label} ({section.products.length})
              </MenuItem>
            ))}
          </TextField>
          {productOrderError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {productOrderError}
            </Alert>
          )}
          {productOrderDraft.length < 2 ? (
            <Alert severity="info">Este grupo tem apenas um produto.</Alert>
          ) : (
            <DragDropContext onDragEnd={handleProductDragEnd}>
              <Droppable droppableId="catalog-product-order">
                {(provided, snapshot) => (
                  <List
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    disablePadding
                    aria-label="Ordem dos produtos"
                    aria-live="polite"
                    sx={{
                      borderRadius: 2,
                      bgcolor: snapshot.isDraggingOver ? 'rgba(99, 102, 241, 0.06)' : 'transparent',
                    }}
                  >
                    {productOrderDraft.map((product, index) => (
                      <Draggable
                        key={product.dbId}
                        draggableId={`catalog-product-${product.dbId}`}
                        index={index}
                        isDragDisabled={savingProductOrder}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <ListItem
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={dragProvided.draggableProps.style}
                            sx={{
                              px: { xs: 0.5, sm: 1 },
                              gap: 1,
                              minHeight: 64,
                              borderBottom: index < productOrderDraft.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider',
                              borderRadius: dragSnapshot.isDragging ? 2 : 0,
                              bgcolor: dragSnapshot.isDragging ? 'background.paper' : 'transparent',
                              boxShadow: dragSnapshot.isDragging ? 6 : 'none',
                            }}
                          >
                            <Box
                              component="span"
                              {...dragProvided.dragHandleProps}
                              aria-label={`Arrastar ${product.itemName}, posição ${index + 1}`}
                              sx={{
                                width: 44,
                                height: 44,
                                flexShrink: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.secondary',
                                borderRadius: '50%',
                                cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab',
                                touchAction: 'none',
                              }}
                            >
                              <DragIndicatorIcon />
                            </Box>
                            <Chip size="small" label={index + 1} aria-label={`Posição ${index + 1}`} />
                            <ListItemText primary={product.itemName} secondary={product.itemDescription || undefined} />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={savingProductOrder} onClick={() => setProductOrderDialogOpen(false)}>Cancelar</Button>
          <Button
            disabled={savingProductOrder || productOrderDraft.length < 2}
            variant="contained"
            onClick={saveProductOrder}
          >
            {savingProductOrder ? 'Salvando...' : 'Salvar ordem'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteGroup)}
        onClose={() => !deletingGroup && setDeleteGroup(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="catalog-group-delete-title"
      >
        <DialogTitle id="catalog-group-delete-title">Excluir grupo e produtos?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação remove o grupo somente do catálogo operacional do Brutos. Ela não altera o cardápio no iFood.
          </Alert>
          <Typography>
            Excluir o grupo <strong>{deleteGroup?.name}</strong> e seus {Number(deleteGroup?.productCount || 0)} produto(s)?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Os produtos deixarão de aparecer no cardápio. Fichas técnicas, overrides e históricos permanecem preservados.
          </Typography>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={deleteGroupConfirmed}
                onChange={(event) => setDeleteGroupConfirmed(event.target.checked)}
              />
            }
            label="Entendo que os produtos deste grupo também serão removidos."
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={deletingGroup} onClick={() => setDeleteGroup(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!deleteGroupConfirmed || deletingGroup}
            onClick={confirmDeleteGroup}
          >
            {deletingGroup ? 'Excluindo...' : 'Excluir grupo e produtos'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sharedGroupManagerOpen}
        onClose={() => !deletingSharedGroup && !selectedSharedGroup && setSharedGroupManagerOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="shared-option-groups-title"
      >
        <DialogTitle id="shared-option-groups-title">Grupos de complementos compartilhados</DialogTitle>
        <DialogContent dividers>
          {sharedOptionGroups.length ? (
            <List disablePadding>
              {sharedOptionGroups.map((group) => (
                <ListItem
                  key={group.id}
                  divider
                  secondaryAction={(
                    <IconButton
                      edge="end"
                      color="error"
                      aria-label={`Excluir ${group.name} de todos os produtos`}
                      disabled={deletingSharedGroup}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSharedGroupToDelete(group);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                >
                  <ListItemButton
                    onClick={() => openSharedGroupDetails(group)}
                    aria-label={`Abrir complementos do grupo ${group.name || 'sem nome'}`}
                    sx={{ py: 1.5, pr: 7 }}
                  >
                    <ListItemText
                      primary={group.name || 'Grupo sem nome'}
                      secondary={`${group.productCount} produto(s) vinculado(s)${group.native ? ' • identidade de origem iFood preservada' : ''}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">Ainda não há grupos de complementos. Crie um dentro da edição de um produto.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSharedGroupManagerOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedSharedGroup && sharedGroupDraft)}
        onClose={() => {
          if (savingSharedGroup) return;
          (sharedGroupDraft?.options || []).forEach((option) => {
            if (option._previewImageUrl) URL.revokeObjectURL?.(option._previewImageUrl);
          });
          setSelectedSharedGroup(null);
          setSharedGroupDraft(null);
          setSharedGroupDraftError('');
        }}
        fullScreen
        aria-labelledby="shared-option-group-details-title"
      >
        <DialogTitle id="shared-option-group-details-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            aria-label="Voltar aos grupos de complementos"
            edge="start"
            disabled={savingSharedGroup}
            onClick={() => {
              (sharedGroupDraft?.options || []).forEach((option) => {
                if (option._previewImageUrl) URL.revokeObjectURL?.(option._previewImageUrl);
              });
              setSelectedSharedGroup(null);
              setSharedGroupDraft(null);
              setSharedGroupDraftError('');
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" component="div">Complementos do grupo</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedSharedGroup?.productCount || 0} produto(s) usam esta definição compartilhada.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
          {sharedGroupDraftError && <Alert severity="error" sx={{ mb: 2 }}>{sharedGroupDraftError}</Alert>}
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Grupo de complementos</Typography>
              <TextField
                label="Nome do grupo"
                value={sharedGroupDraft?.name || ''}
                onChange={(event) => updateSharedGroupDraft({ name: event.target.value })}
                fullWidth
                margin="dense"
                disabled={savingSharedGroup || !mayMutateCatalog}
              />
              <TextField
                label="Descrição do grupo"
                value={sharedGroupDraft?.description || ''}
                onChange={(event) => updateSharedGroupDraft({ description: event.target.value })}
                fullWidth
                margin="dense"
                multiline
                minRows={2}
                disabled={savingSharedGroup || !mayMutateCatalog}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <TextField
                  label="Quantidade mínima"
                  type="number"
                  value={sharedGroupDraft?.minQuantity ?? 0}
                  onChange={(event) => updateSharedGroupDraft({ minQuantity: Math.max(0, Number(event.target.value) || 0) })}
                  inputProps={{ min: 0 }}
                  disabled={savingSharedGroup || !mayMutateCatalog}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Quantidade máxima"
                  type="number"
                  value={sharedGroupDraft?.maxQuantity ?? 1}
                  onChange={(event) => updateSharedGroupDraft({ maxQuantity: Math.max(0, Number(event.target.value) || 0) })}
                  inputProps={{ min: 0 }}
                  disabled={savingSharedGroup || !mayMutateCatalog}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Typography variant="h6">Complementos ({sharedGroupDraft?.options?.length || 0})</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={savingSharedGroup || !mayMutateCatalog}
                onClick={addSharedOptionDraft}
              >
                Criar complemento
              </Button>
            </Box>
            {(sharedGroupDraft?.options || []).map((option, optionIndex) => {
              const imageUrl = option._previewImageUrl || resolveCatalogImageUrl(option);
              const priceValue = Number(option.price?.value ?? option.price ?? option.itemPrice?.value ?? option.itemPrice ?? 0);
              return (
                <Paper key={option.brutosSharedOptionId || option._draftOptionId || optionIndex} variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Complemento {optionIndex + 1}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        aria-label={`Copiar complemento ${option.name || optionIndex + 1}`}
                        title="Copiar complemento"
                        disabled={savingSharedGroup || !mayMutateCatalog}
                        onClick={() => duplicateSharedOptionDraft(optionIndex)}
                      >
                        <ContentCopyOutlinedIcon />
                      </IconButton>
                      <IconButton
                        aria-label={`Excluir complemento ${option.name || optionIndex + 1}`}
                        title="Excluir complemento"
                        color="error"
                        disabled={savingSharedGroup || !mayMutateCatalog}
                        onClick={() => setSharedGroupDraft((current) => ({
                          ...current,
                          options: current.options.filter((_, index) => index !== optionIndex),
                        }))}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1.5 }}>
                    <TextField
                      label="Nome do complemento"
                      value={option.name || ''}
                      onChange={(event) => updateSharedOptionDraft(optionIndex, { name: event.target.value })}
                      fullWidth
                      disabled={savingSharedGroup || !mayMutateCatalog}
                    />
                    <TextField
                      label="Preço"
                      type="number"
                      value={priceValue}
                      onChange={(event) => updateSharedOptionDraft(optionIndex, {
                        price: { ...(option.price && typeof option.price === 'object' ? option.price : {}), value: Math.max(0, Number(event.target.value) || 0) },
                      })}
                      inputProps={{ min: 0, step: '0.01' }}
                      fullWidth
                      disabled={savingSharedGroup || !mayMutateCatalog}
                    />
                  </Box>
                  <TextField
                    label="Descrição"
                    value={option.description || ''}
                    onChange={(event) => updateSharedOptionDraft(optionIndex, { description: event.target.value })}
                    fullWidth
                    margin="normal"
                    multiline
                    minRows={2}
                    disabled={savingSharedGroup || !mayMutateCatalog}
                  />
                  <TextField
                    label="Código externo / PDV"
                    value={option.externalCode || 'Será gerado ao salvar um novo complemento'}
                    fullWidth
                    margin="dense"
                    disabled
                    helperText={option.externalCodeOrigin === 'brutos-local' ? 'Código COMP compartilhado entre os produtos vinculados.' : 'Identidade de origem preservada; cópias recebem um novo código COMP.'}
                  />
                  <FormControlLabel
                    sx={{ mt: 0.5, mb: 1 }}
                    control={(
                      <Checkbox
                        checked={ensureShowInKds(option.showInKds)}
                        onChange={(event) => updateSharedOptionDraft(optionIndex, { showInKds: event.target.checked })}
                        disabled={savingSharedGroup || !mayMutateCatalog}
                      />
                    )}
                    label="Mostrar este complemento no KDS"
                  />
                  <Divider sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {imageUrl ? (
                      <Box component="img" src={imageUrl} alt={option.name || 'Complemento'} sx={{ width: 112, height: 84, objectFit: 'cover', borderRadius: 1, border: '1px solid #dbe2ea' }} />
                    ) : <Typography variant="body2" color="text.secondary">Sem foto</Typography>}
                    <Box>
                      <Button component="label" variant="outlined" size="small" disabled={savingSharedGroup || !mayMutateCatalog || Number(selectedSharedGroup?.productCount || 0) <= 0}>
                        Escolher foto
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            const previewUrl = URL.createObjectURL?.(file) || '';
                            const oldPreview = sharedGroupDraft?.options?.[optionIndex]?._previewImageUrl;
                            if (oldPreview) URL.revokeObjectURL?.(oldPreview);
                            updateSharedOptionDraft(optionIndex, { _pendingImageFile: file, _previewImageUrl: previewUrl });
                            event.target.value = '';
                          }}
                        />
                      </Button>
                      {Number(selectedSharedGroup?.productCount || 0) <= 0 && (
                        <Typography display="block" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Associe o grupo a um produto para enviar uma foto.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            {!sharedGroupDraft?.options?.length && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                Este grupo ainda não tem complementos. Use “Criar complemento” para adicionar o primeiro.
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            disabled={savingSharedGroup}
            onClick={() => {
              (sharedGroupDraft?.options || []).forEach((option) => {
                if (option._previewImageUrl) URL.revokeObjectURL?.(option._previewImageUrl);
              });
              setSelectedSharedGroup(null);
              setSharedGroupDraft(null);
              setSharedGroupDraftError('');
            }}
          >
            Cancelar
          </Button>
          <Button variant="contained" disabled={savingSharedGroup || !mayMutateCatalog} onClick={saveSharedGroupDetails}>
            {savingSharedGroup ? <CircularProgress size={20} color="inherit" /> : 'Salvar alterações'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(sharedGroupToDelete)}
        onClose={() => !deletingSharedGroup && setSharedGroupToDelete(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="shared-option-group-delete-title"
      >
        <DialogTitle id="shared-option-group-delete-title">Excluir grupo compartilhado?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            O grupo e seus complementos serão removidos localmente de todos os produtos vinculados. O iFood não será alterado.
          </Alert>
          <Typography>
            Excluir <strong>{sharedGroupToDelete?.name}</strong> de {Number(sharedGroupToDelete?.productCount || 0)} produto(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={deletingSharedGroup} onClick={() => setSharedGroupToDelete(null)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={deletingSharedGroup} onClick={deleteSharedOptionGroup}>
            {deletingSharedGroup ? 'Excluindo...' : 'Excluir para todos'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedProduct}
        onClose={savingProduct || duplicatingProduct ? undefined : closeProductDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isAddingProduct ? 'Adicionar produto' : selectedProduct?.itemName}
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              {productFormError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {productFormError}
                </Alert>
              )}
              {!isAddingProduct ? (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedProduct.isPizza ? (
                    <Chip size="small" color="primary" variant="outlined" label="Pizza iFood" />
                  ) : null}
                  <Chip
                    size="small"
                    color={Number(selectedProduct.technicalSheetCount || 0) > 0 ? 'success' : 'warning'}
                    label={Number(selectedProduct.technicalSheetCount || 0) > 0 ? `Ficha técnica: ${selectedProduct.technicalSheetCount} insumo(s)` : 'Produto sem ficha técnica'}
                  />
                </Box>
              ) : null}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Imagem do Produto
                </Typography>
                {(() => {
                  const currentLogosUrls = editingProduct ? editedProduct.logosUrls : selectedProduct.logosUrls;
                  const imageUrl = editingProduct && editedProduct._previewImageUrl
                    ? editedProduct._previewImageUrl
                    : buildIfoodImageUrl(currentLogosUrls);
                  return imageUrl ? (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img
                        src={resolveCatalogImageUrl(imageUrl)}
                        alt={selectedProduct.itemName}
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                      />
                    </Box>
                  ) : null;
                })()}
                {editingProduct && (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const previewUrl = URL.createObjectURL(file);
                        productPreviewUrlsRef.current.push(previewUrl);
                        setEditedProduct((previous) => ({
                          ...previous,
                          _pendingImageFile: file,
                          _previewImageUrl: previewUrl,
                        }));
                      }
                    }}
                    style={{ marginBottom: '16px' }}
                  />
                )}
              </Box>
              <TextField
                label="Nome do Produto"
                fullWidth
                margin="normal"
                value={editingProduct ? editedProduct.itemName || '' : selectedProduct.itemName}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, itemName: e.target.value }))}
                disabled={!editingProduct}
              />
              <TextField
                label="Nome no KDS"
                fullWidth
                margin="normal"
                value={editingProduct ? editedProduct.kdsName || '' : selectedProduct.kdsName || ''}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, kdsName: e.target.value }))}
                disabled={!editingProduct}
              />
              <TextField
                select
                label="Grupo"
                fullWidth
                margin="normal"
                value={editingProduct ? editedProduct.catalogGroupId || '' : selectedProduct.catalogGroupId || ''}
                onChange={(event) => {
                  const nextGroupId = Number(event.target.value) || null;
                  const nextGroup = groups.find((group) => Number(group.id) === nextGroupId);
                  setEditedProduct((previous) => ({
                    ...previous,
                    catalogGroupId: nextGroupId,
                    categoryName: nextGroup?.name || '',
                  }));
                }}
                disabled={!editingProduct}
                helperText={groups.length ? 'O nome do grupo é resolvido pelo servidor.' : 'Crie um grupo antes de classificar o produto.'}
              >
                <MenuItem value="">Sem grupo</MenuItem>
                {orderedGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Descrição"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                value={editingProduct ? editedProduct.itemDescription || '' : selectedProduct.itemDescription || ''}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, itemDescription: e.target.value }))}
                disabled={!editingProduct}
              />
              <TextField
                label="Instruções de Preparo"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={editingProduct ? editedProduct.preparationDescription || '' : selectedProduct.preparationDescription || ''}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, preparationDescription: e.target.value }))}
                disabled={!editingProduct}
                helperText="Use este campo para descrever o preparo do produto para uso interno."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingProduct ? ensureShowInKds(editedProduct.showInKds) : ensureShowInKds(selectedProduct.showInKds)}
                    onChange={(e) => setEditedProduct(prev => ({ ...prev, showInKds: e.target.checked }))}
                    disabled={!editingProduct}
                  />
                }
                label="Mostrar este produto no KDS"
                sx={{ mt: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingProduct ? ensureShowInBalcao(editedProduct.showInBalcao) : ensureShowInBalcao(selectedProduct.showInBalcao)}
                    onChange={(e) => setEditedProduct(prev => ({ ...prev, showInBalcao: e.target.checked }))}
                    disabled={!editingProduct}
                  />
                }
                label="Mostrar este produto no Balcão"
              />
              <TextField
                label="Preço"
                fullWidth
                margin="normal"
                type="number"
                value={editingProduct ? editedProduct.itemPrice?.value || '' : selectedProduct.itemPrice?.value || ''}
                onChange={(e) => setEditedProduct(prev => ({
                  ...prev,
                  itemPrice: { value: parseFloat(e.target.value) || 0 }
                }))}
                disabled={!editingProduct}
              />
              <TextField
                label="Preço Mínimo de Venda"
                fullWidth
                margin="normal"
                type="number"
                value={editingProduct ? editedProduct.itemMinSalePrice || '' : selectedProduct.itemMinSalePrice || ''}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, itemMinSalePrice: parseFloat(e.target.value) || 0 }))}
                disabled={!editingProduct}
              />
              <TextField
                label="Código Externo"
                fullWidth
                margin="normal"
                value={editingProduct ? editedProduct.itemExternalCode || '' : selectedProduct.itemExternalCode || ''}
                disabled
                helperText={isAddingProduct
                  ? 'Código PDV PROD gerado pelo Brutos ao salvar; não é enviado ao iFood.'
                  : selectedProduct.itemExternalCodeOrigin === 'brutos-local'
                    ? 'Código PDV local do Brutos; não enviado ao iFood.'
                    : 'Código preservado para integrações e importações.'}
              />
              <TextField
                label="Quantidade"
                fullWidth
                margin="normal"
                type="number"
                value={editingProduct ? editedProduct.itemQuantity || '' : selectedProduct.itemQuantity || ''}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, itemQuantity: parseFloat(e.target.value) || 0 }))}
                disabled={!editingProduct}
              />
              <TextField
                label="Unidade"
                fullWidth
                margin="normal"
                value={editingProduct ? editedProduct.itemUnit || '' : selectedProduct.itemUnit || ''}
                onChange={(e) => setEditedProduct(prev => ({ ...prev, itemUnit: e.target.value }))}
                disabled={!editingProduct}
              />
              {(editingProduct || selectedProduct.itemOptionGroups?.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" gutterBottom>Complementos</Typography>
                    {editingProduct && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setEditedProduct((previous) => ({
                            ...previous,
                            itemOptionGroups: [...(previous.itemOptionGroups || []), createSharedOptionGroupDraft()],
                          }))}
                        >
                          Criar grupo
                        </Button>
                        <TextField
                          select
                          size="small"
                          label="Reaproveitar grupo"
                          value={groupToAttach}
                          onChange={(event) => setGroupToAttach(event.target.value)}
                          sx={{ minWidth: 230 }}
                        >
                          <MenuItem value="">Selecione um grupo</MenuItem>
                          {sharedOptionGroups
                            .filter((candidate) => !(editedProduct.itemOptionGroups || []).some((group) => group.brutosSharedGroupId === candidate.id))
                            .map((candidate) => (
                              <MenuItem key={candidate.id} value={candidate.id}>
                                {candidate.name || 'Grupo sem nome'} ({candidate.productCount})
                              </MenuItem>
                            ))}
                        </TextField>
                        <Button
                          size="small"
                          disabled={!groupToAttach}
                          onClick={() => {
                            const candidate = sharedOptionGroups.find((group) => group.id === groupToAttach);
                            if (!candidate) return;
                            setEditedProduct((previous) => ({
                              ...previous,
                              itemOptionGroups: [...(previous.itemOptionGroups || []), cloneProductDraft({ itemOptionGroups: [candidate.definition] }).itemOptionGroups[0]],
                            }));
                            setGroupToAttach('');
                          }}
                        >
                          Adicionar
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {(editingProduct ? editedProduct.itemOptionGroups || [] : selectedProduct.itemOptionGroups || []).map((group, groupIndex) => {
                    const groupUsage = sharedOptionGroups.find((candidate) => candidate.id === group.brutosSharedGroupId);
                    return (
                    <Box key={group.brutosSharedGroupId || group.optionGroupId || groupIndex} sx={{ mb: 2, pl: 2, borderLeft: '2px solid #e2e8f0' }}>
                      {editingProduct && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={groupUsage
                              ? `Compartilhado • ${groupUsage.productCount} produto(s)`
                              : 'Grupo compartilhado após salvar'}
                          />
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setEditedProduct((previous) => ({
                              ...previous,
                              itemOptionGroups: (previous.itemOptionGroups || []).filter((_, index) => index !== groupIndex),
                            }))}
                          >
                            Remover deste produto
                          </Button>
                        </Box>
                      )}
                      <TextField
                        label="Nome do Grupo"
                        fullWidth
                        margin="normal"
                        value={group.name}
                        onChange={(e) => {
                          setEditedProduct((previous) => updateProductOptionGroup(previous, groupIndex, { name: e.target.value }));
                        }}
                        disabled={!editingProduct}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Quantidade Mínima"
                          type="number"
                          value={group.minQuantity}
                          onChange={(e) => {
                            setEditedProduct((previous) => updateProductOptionGroup(previous, groupIndex, { minQuantity: parseInt(e.target.value) || 0 }));
                          }}
                          disabled={!editingProduct}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Quantidade Máxima"
                          type="number"
                          value={group.maxQuantity}
                          onChange={(e) => {
                            setEditedProduct((previous) => updateProductOptionGroup(previous, groupIndex, { maxQuantity: parseInt(e.target.value) || 0 }));
                          }}
                          disabled={!editingProduct}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                      {(group.options || []).map((option, optionIndex) => {
                        const optionImageUrl = option._previewImageUrl || resolveCatalogImageUrl(option);
                        return (
                          <Box key={option.brutosSharedOptionId || option.optionId || optionIndex} sx={{ ml: 2, mb: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                            {editingProduct && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteOutlineIcon />}
                                  onClick={() => setEditedProduct((previous) => ({
                                    ...previous,
                                    itemOptionGroups: (previous.itemOptionGroups || []).map((currentGroup, currentIndex) => (
                                      currentIndex === groupIndex
                                        ? { ...currentGroup, options: (currentGroup.options || []).filter((_, index) => index !== optionIndex) }
                                        : currentGroup
                                    )),
                                  }))}
                                >
                                  Excluir complemento compartilhado
                                </Button>
                              </Box>
                            )}
                            <TextField
                              label="Nome da Opção"
                              fullWidth
                              margin="dense"
                              value={option.name}
                              onChange={(e) => {
                                setEditedProduct((previous) => updateProductOption(previous, groupIndex, optionIndex, { name: e.target.value }));
                              }}
                              disabled={!editingProduct}
                            />
                          <TextField
                            label="Código Externo"
                            fullWidth
                            margin="dense"
                            value={option.externalCode}
                            disabled
                            helperText={option.externalCodeOrigin === 'brutos-local'
                              ? 'Código PDV COMP local do Brutos; não enviado ao iFood.'
                              : option.externalCode || option.optionId || option.id
                                ? 'Código preservado do catálogo iFood.'
                                : 'Código PDV COMP gerado pelo Brutos ao salvar.'}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editingProduct ? ensureShowInKds(option.showInKds) : ensureShowInKds(option.showInKds)}
                                onChange={(e) => {
                                  setEditedProduct((previous) => updateProductOption(previous, groupIndex, optionIndex, {
                                    showInKds: e.target.checked,
                                  }));
                                }}
                                disabled={!editingProduct}
                              />
                            }
                            label="Mostrar este complemento no KDS"
                            sx={{ mb: 1 }}
                          />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                              {optionImageUrl ? (
                                <Box
                                  component="img"
                                  src={optionImageUrl}
                                  alt={option.name || 'Complemento'}
                                  sx={{
                                    width: 96,
                                    height: 72,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: '#f8fafc',
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Sem imagem
                                </Typography>
                              )}
                              {editingProduct && (
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const previewUrl = URL.createObjectURL(file);
                                      productPreviewUrlsRef.current.push(previewUrl);
                                      setEditedProduct((previous) => updateProductOption(previous, groupIndex, optionIndex, {
                                        _pendingImageFile: file,
                                        _previewImageUrl: previewUrl,
                                      }));
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          <TextField
                            label="Descrição"
                            fullWidth
                            margin="dense"
                            value={option.description || ''}
                            onChange={(e) => {
                              setEditedProduct((previous) => updateProductOption(previous, groupIndex, optionIndex, { description: e.target.value }));
                            }}
                            disabled={!editingProduct}
                          />
                          <TextField
                            label="Preço"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={option.price?.value || ''}
                            onChange={(e) => {
                              setEditedProduct((previous) => updateProductOption(previous, groupIndex, optionIndex, {
                                price: { value: parseFloat(e.target.value) || 0 }
                              }));
                            }}
                            disabled={!editingProduct}
                          />
                          </Box>
                        );
                      })}
                      {editingProduct && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setEditedProduct((previous) => ({
                            ...previous,
                            itemOptionGroups: (previous.itemOptionGroups || []).map((currentGroup, index) => (
                              index === groupIndex
                                ? { ...currentGroup, options: [...(currentGroup.options || []), createSharedOptionDraft()] }
                                : currentGroup
                            )),
                          }))}
                        >
                          Adicionar complemento
                        </Button>
                      )}
                    </Box>
                  );})}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!editingProduct ? (
            <>
              {mayManageCatalog && (
                <Button disabled={catalogReadOnly || loadingProductEditor} onClick={startEditingProduct} variant="contained">
                  {loadingProductEditor ? 'Carregando...' : 'Editar'}
                </Button>
              )}
              {mayManageCatalog && selectedProduct?.dbId && (
                <Button
                  disabled={catalogReadOnly || duplicatingProduct}
                  onClick={duplicateSelectedProduct}
                  startIcon={<ContentCopyOutlinedIcon />}
                  variant="outlined"
                >
                  {duplicatingProduct ? 'Duplicando...' : 'Duplicar produto'}
                </Button>
              )}
              <Button
                onClick={() => setTechnicalSheetOpen(true)}
                variant="outlined"
                startIcon={<MenuBookOutlinedIcon />}
              >
                Ficha técnica
              </Button>
              {mayManageCatalog && (
                <Button
                  color="error"
                  disabled={catalogReadOnly || deletingProduct || duplicatingProduct}
                  onClick={deleteSelectedProduct}
                  startIcon={<DeleteOutlineIcon />}
                  variant="outlined"
                >
                  {deletingProduct ? 'Excluindo...' : 'Excluir'}
                </Button>
              )}
              <Button
                disabled={loadingProductEditor}
                onClick={addSelectedProductToBalcao}
                variant="outlined"
              >
                {loadingProductEditor ? 'Carregando...' : 'Adicionar ao Balcão'}
              </Button>
              <Button disabled={duplicatingProduct} onClick={() => setSelectedProduct(null)}>Fechar</Button>
            </>
          ) : (
            <>
              <Button disabled={savingProduct} onClick={cancelEditingProduct}>Cancelar</Button>
              <Button
                disabled={savingProduct || !String(editedProduct.itemName || '').trim()}
                onClick={saveEditingProduct}
                variant="contained"
              >
                {savingProduct ? 'Salvando...' : 'Salvar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ProductTechnicalSheetDialog
        open={technicalSheetOpen}
        product={selectedProduct}
        onClose={() => {
          setTechnicalSheetOpen(false);
          fetchProducts({ silent: true });
        }}
      />
      </Box>
    </ThemeProvider>
  );
};

export default Cardapio;
